// NPM
import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Checkbox,
  Button,
  Stack,
  Typography,
  InputLabel,
  FormControlLabel,
} from '@mui/material';

// COMPONENTS
import ListItem from './DistributionListItem';
import Paginator from '../../../../components/common/Paginator';
import DistibutionSingle from './DistributionSingle';
import Loader from '../../../common/Loader';
import CustomModal from '../../../common/CustomModal';
import DistributionAddEdit from './DistributionAddEdit';

// CONFIG
import config from '../../../../Config';

// FORMATTED MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// NETWORKING
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

const DistributionLists = (props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL ?? 10);
  const [searchValue, setSearchValue] = useState({});
  const [distLists, setDistLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [deleteLists, setDeleteLists] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalWidth, setModalWidth] = useState(450);
  const [modalType, setModalType] = useState(null);
  const { getDistLists, deleteSelectedDistLists } = useAddressBookRequest();

  const getData = useCallback(async () => {
    setLoading(true);
    const distLists = await getDistLists(props.errorHandler);
    console.log('dist list data:', distLists);
    setDistLists(distLists);
    setLoading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  /*
    Handles the change event when clicking a pagination number
  */
  const changeEvent = (value) => {
    setPageNumber(value);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const handleListSearchChange = async (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    let updatedSearchValue = {
      ...searchValue,
      [name]: value,
    };
    if (name === 'submit') updatedSearchValue = searchValue;
    console.log('edits:', updatedSearchValue);

    if (name === 'searchContent') {
      setSearchValue(updatedSearchValue);
    } else {
      handleSubmit(updatedSearchValue);
    }
  };

  const handleKeyDown = async (event) => {
    const { key } = event;
    if (key === 'Enter') handleSubmit(searchValue);
  };

  const handleSubmit = async (updatedSearchValue) => {
    setLoading(true);
    const data = await getDistLists(props.errorHandler, updatedSearchValue);
    setDistLists(data);
    setSearchValue(updatedSearchValue);
    setLoading(false);
    setPageNumber(1);
  };

  const generateButtons = () => {
    return (
      <Stack sx={{ marginTop: 4, marginBottom: 1, gap: 1 }}>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            {/* <Button
            variant="contained"
            color="secondary"
            onClick={props.showDistListImporter}>
            {AddressBookFormattedMessages.importDistList}
          </Button> */}

            <Button
              variant="contained"
              onClick={() => {
                setSelectedList(false);
                setOpenModal(true);
                setModalType('addEdit');
                setModalWidth(450);
              }}
              color="secondary"
            >
              {AddressBookFormattedMessages.createNewButton}
            </Button>
          </Stack>

          {!selectedList && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={deleteLists.length === 0}
              onClick={() => {
                setOpenModal(true);
                setModalType('delete');
              }}
            >
              Delete Selected
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const selectRowsToBeDeleted = (id) => {
    setDeleteLists((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedDistLists = async () => {
    setLoading(true);
    await deleteSelectedDistLists(deleteLists, props.errorHandler);
    const data = await getDistLists(props.errorHandler);
    setDeleteLists([]);
    setDistLists(data);
    setSelectedList(null);
    setLoading(false);
    backToTopLevel();
  };

  const generateTableRows = (displayList) => {
    return displayList.map((list, i) => {
      return (
        <ListItem
          id={list.distribution_list_id}
          key={i}
          list={list}
          onClick={() => setSelectedList(list)}
          selectRowsToBeDeleted={selectRowsToBeDeleted}
          accessLevel={props.accessLevel}
          accessType={props.accessType}
          deleteLists={deleteLists}
        />
      );
    });
  };

  const backToTopLevel = (refreshData) => {
    setSelectedList(false);
    setOpenModal(false);
    setModalWidth(450);
    setModalType(null);
    if (refreshData) getData();
  };

  const generateTable = () => {
    if (loading) return <Loader />;
    if (!distLists || !distLists.length) return 'No distribution lists found.';

    const displayList = distLists.slice(
      (pageNumber - 1) * pageLength,
      (pageNumber - 1) * pageLength + pageLength
    );

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell style={{ width: '35%' }}>
                  {AddressBookFormattedMessages.listName}
                </TableCell>
                <TableCell style={{ width: '25%' }}>
                  {AddressBookFormattedMessages.type}
                </TableCell>
                <TableCell style={{ width: '25%' }}>
                  {AddressBookFormattedMessages.quantityOfContacts}
                </TableCell>
                <TableCell align="right">
                  {AddressBookFormattedMessages.select}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{generateTableRows(displayList)}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          itemCount={distLists.length}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          pageLength={pageLength}
          changeEvent={changeEvent}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Box>
    );
  };

  return (
    <Fragment>
      <Box
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '0 0 20px 20px',
          paddingX: 4,
          paddingY: 2,
          maxWidth: 800,
        }}
      >
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            justifyContent: 'space-between',
            alignItems: { md: 'center' },
            gap: 2,
          }}
        >
          <Stack sx={{ flex: 1 }}>
            <InputLabel>{AddressBookFormattedMessages.searchByName}</InputLabel>

            <TextField
              id="listSearch"
              autoFocus
              type="text"
              name="searchContent"
              onKeyDown={handleKeyDown}
              onChange={handleListSearchChange}
              className="search-input"
              variant="outlined"
              size="small"
            />
          </Stack>

          <Stack sx={{ gap: 1 }}>
            Filter
            <Stack sx={{ flexDirection: 'row', gap: 2, paddingLeft: 1.1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="personal"
                    value="personal"
                    color="secondary"
                    onChange={handleListSearchChange}
                    id="P"
                    sx={{ padding: 0 }}
                  />
                }
                label={AddressBookFormattedMessages.personal}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="global"
                    value="global"
                    onChange={handleListSearchChange}
                    id="G"
                    color="secondary"
                    sx={{ padding: 0 }}
                  />
                }
                label={AddressBookFormattedMessages.global}
              />
            </Stack>
          </Stack>

          <Box>
            <Button
              variant="outlined"
              color="secondary"
              name="submit"
              onClick={handleListSearchChange}
            >
              Search
            </Button>
          </Box>
        </Stack>
      </Box>
      {generateButtons()}

      {selectedList ? (
        <DistibutionSingle
          backFunction={backToTopLevel}
          selected={selectedList}
          errorHandler={props.errorHandler}
          setOpenEditModal={setOpenModal}
          setParentModalType={setModalType}
        />
      ) : (
        <>{generateTable()}</>
      )}

      <CustomModal
        title="Lists"
        icon="userlist"
        open={openModal}
        setOpen={setOpenModal}
        width={modalWidth}
      >
        {modalType == 'addEdit' && (
          <DistributionAddEdit
            errorHandler={props.errorHandler}
            backFunction={backToTopLevel}
            accessLevel={props.accessLevel}
            accessType={props.accessType}
            selected={selectedList}
            setModalWidth={setModalWidth}
            personal={searchValue.personal && !searchValue.global}
            global={searchValue.global && !searchValue.personal}
          />
        )}

        {modalType == 'delete' && (
          <Box>
            <Typography gutterBottom>
              {`${deleteLists.length} ${
                deleteLists.length === 1 ? 'List' : 'Lists '
              } Selected`}
            </Typography>

            <Typography gutterBottom>
              {AddressBookFormattedMessages.deleteListsBody}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Button
                onClick={() => backToTopLevel(false)}
                variant="link"
                color="secondary"
              >
                {AddressBookFormattedMessages.cancelText}
              </Button>

              <Button
                onClick={handleDeleteSelectedDistLists}
                variant="contained"
                color="secondary"
              >
                {AddressBookFormattedMessages.deleteText}
              </Button>
            </Box>
          </Box>
        )}
      </CustomModal>
    </Fragment>
  );
};

export default DistributionLists;
